import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IconButton, CircularProgress } from '@mui/material';
import BadgeAvatar from '@src/components/BadgeAvatar';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import actions from '@src/redux/actions';
import {
  BLOCK_STATUS,
  BLOCK_STATUS_LABEL,
  CONVERTING_STATUS,
  EXTRACTION_STATUS,
  PROJECT_PRODUCT,
} from '@src/constants/project';
import Tooltip from '@src/components/Tooltip';
import DropdownCustom from '@src/new-components/Dropdown';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useTrackProjectFilterApplied from '@src/hooks/useTrackProjectFilterApplied';
import { MOE_FILTER_TYPE } from '@src/constants/moengage';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import useHorizontalOverflow from '@src/hooks/useHorizontalOverflow';
import {
  isNoteChanged,
  hasNarrationConfig,
  countExtractedBlocks,
  getExportingState,
} from '@src/services/project';
import pluralize from '@src/utils/pluralize';
import { StyledItemVoice, StyledToolbar } from './index.style';
import ConfigMultiBlock from './ConfigMultiBlock';
import AIGenerateButton from './AIGenerateButton';
import AIGenerateDialog from './AIGenerateDialog';

const Toolbar = ({
  handleConvertBlockSelected,
  filterBlockResults,
  aiDialogOpen,
  aiDialogTargetBlockId,
  onOpenAIGenerateDialog,
  onCloseAIGenerateDialog,
  onSaveProject,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();

  const selectedBlockIds = useSelector(
    (state) => state.project.selectedBlockIds,
  );
  const currentFilteringVoiceCode = useSelector(
    (state) => state.project.currentFilteringVoiceCode,
  );

  const {
    currentFilteringStatus,
    multiBlock,
    voiceDefault,
    source,
    isLoadingProjectDetail,
    isGeneratingNarration,
    exports,
    isDownloading,
  } = useSelector((state) => state.project);
  const { blocks = {}, blockIds = [] } = multiBlock || {};
  const user = useSelector((state) => state.auth.user);
  const trackFilterApplied = useTrackProjectFilterApplied();
  const { getFeatureValue } = useFeatureFlags();
  const slideV2Enabled = getFeatureValue(FEATURE_KEYS.PRESENTATION_SLIDE_V2, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });
  const extractProgressEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXTRACT_PROGRESS,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );
  const isSkeletonUI =
    extractProgressEnabled &&
    source?.extraction?.response?.status === EXTRACTION_STATUS.PENDING;

  const extractionProgress = useMemo(
    () => (isSkeletonUI ? countExtractedBlocks(blocks, source) : null),
    [isSkeletonUI, blocks, source],
  );

  const {
    ref: scrollRef,
    canScrollLeft,
    canScrollRight,
    scrollByStep,
  } = useHorizontalOverflow([
    selectedBlockIds?.length,
    slideV2Enabled,
    isSkeletonUI,
  ]);

  const numBlockForEachStatus = useMemo(() => {
    // Make an object for each key is block status and value is number of block for that status
    const blockStatusCount = Object.keys(BLOCK_STATUS).reduce((acc, key) => {
      let arrOfBlocks = Object.values(blocks);
      if (currentFilteringVoiceCode) {
        arrOfBlocks = arrOfBlocks.filter(
          (block) =>
            (block.voice?.code || voiceDefault?.code) ===
            currentFilteringVoiceCode,
        );
      }
      if (key === BLOCK_STATUS.ALL) {
        acc[key] = arrOfBlocks.length;
        return acc;
      }

      const arr = arrOfBlocks.filter((block) => block.blockStatus === key);

      acc[key] = arr.length;
      return acc;
    }, {});

    return blockStatusCount;
  }, [blocks, currentFilteringVoiceCode, voiceDefault]);

  const getNumberOfVoices = useCallback(
    (voiceCode) => {
      if (currentFilteringStatus === BLOCK_STATUS.ALL) {
        return true;
      }

      const blockGroupByStatus = Object.keys(BLOCK_STATUS).reduce(
        (acc, key) => {
          let arrOfBlocks = Object.values(blocks);
          if (currentFilteringStatus) {
            arrOfBlocks = arrOfBlocks.filter(
              (block) => block.blockStatus === currentFilteringStatus,
            );
          }

          if (key === BLOCK_STATUS.ALL) {
            acc[key] = arrOfBlocks;
            return acc;
          }

          const arr = arrOfBlocks.filter((block) => block.blockStatus === key);

          acc[key] = arr;
          return acc;
        },
        {},
      );

      if (voiceCode === '') {
        return Object.keys(filterBlockResults)?.length;
      }

      return (
        blockGroupByStatus?.[currentFilteringStatus]?.filter(
          (block) => (block?.voice?.code || voiceDefault?.code) === voiceCode,
        )?.length || 0
      );
    },
    [voiceDefault, currentFilteringStatus, blocks, filterBlockResults],
  );

  const blockStatusOptions = useMemo(() => {
    const baseOptions = Object.values(BLOCK_STATUS_LABEL).map((item) => ({
      label: `${t(item.label)} (${numBlockForEachStatus[item.value]})`,
      value: item.value,
      disabled: numBlockForEachStatus[item.value] === 0,
    }));

    return baseOptions;
  }, [numBlockForEachStatus, t]);

  const handleChangeBlockStatus = (value) => {
    if (value !== BLOCK_STATUS.ALL)
      trackFilterApplied({
        filterType: MOE_FILTER_TYPE.STATUS,
        filterValue: value,
      });
    dispatch(actions.project.setFilterStatus(value));
    dispatch(actions.project.setStartFilterBlock(true));
  };

  useEffect(
    () => () => {
      dispatch(actions.project.setFilterStatus(BLOCK_STATUS.ALL));
      dispatch(actions.project.setFilterVoiceCode(''));
    },
    [],
  );

  const voicesUsed = useMemo(() => {
    const arrOfBlocks = Object.values(blocks);
    const isAllBlocksHaveVoice = arrOfBlocks.every(
      (block) => block.voice?.code,
    );

    const codes = new Set();
    // Always start with "All block voices"
    const uniqueVoices = [
      {
        code: '',
        name: 'allBlockVoices',
        roundImage: '',
        language: null,
        type: 'all',
      },
    ];
    codes.add('');

    // Add voiceDefault if it has a unique code and is defined
    if (
      voiceDefault?.code &&
      !codes.has(voiceDefault.code) &&
      !isAllBlocksHaveVoice
    ) {
      uniqueVoices.push({
        code: voiceDefault.code,
        name: voiceDefault.name,
        roundImage: voiceDefault.roundImage,
        language: voiceDefault.language,
      });
      codes.add(voiceDefault.code);
    }

    // Add all unique block voices (except already added ones)
    Object.values(blocks).forEach((block) => {
      const { voice } = block;
      if (voice?.code && !codes.has(voice.code)) {
        uniqueVoices.push(voice);
        codes.add(voice.code);
      }
    });

    return uniqueVoices;
  }, [blocks, t, voiceDefault, currentFilteringVoiceCode]);

  // Get the current filtering voice
  const currentFilteringVoice = useMemo(
    () => voicesUsed?.find((item) => item?.code === currentFilteringVoiceCode),
    [voicesUsed, currentFilteringVoiceCode],
  );

  const handleRefresh = () => {
    if (!currentFilteringVoice) {
      dispatch(actions.project.setFilterVoiceCode(''));
    }

    dispatch(actions.project.setStartFilterBlock(true));
  };

  const handleAIGenerateClick = useCallback(() => {
    onOpenAIGenerateDialog?.();
  }, [onOpenAIGenerateDialog]);

  const narrationConfigExists = hasNarrationConfig(source);
  const anyNoteChanged = Object.values(blocks).some(isNoteChanged);

  const anyBlockConverting = Object.values(blocks).some(
    (block) => block?.status === CONVERTING_STATUS.IN_PROGRESS,
  );

  const exportHistoryEnabled = useExportHistoryEnabled();

  const exportBannerEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXPORT_VIDEO,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );
  const { isExporting } = getExportingState({
    exports,
    isDownloading,
    product: PROJECT_PRODUCT.PRESENTATION,
  });

  const aiButtonLoading = isGeneratingNarration;
  const aiButtonDisabled =
    isGeneratingNarration ||
    anyBlockConverting ||
    isLoadingProjectDetail ||
    !blockIds.length ||
    (exportBannerEnabled && isExporting && !exportHistoryEnabled) ||
    (narrationConfigExists && !anyNoteChanged);

  const startIconFilterBlockVoice = useMemo(() => {
    if (currentFilteringVoiceCode === '' || !currentFilteringVoice) {
      return (
        <ManageAccountsRoundedIcon
          sx={{ width: 24, height: 24, color: COLOR.navyBlue }}
        />
      );
    }

    return (
      <BadgeAvatar
        width={16}
        smallImgWidth={10}
        img={currentFilteringVoice?.roundImage}
        smallImg={
          currentFilteringVoice?.language &&
          currentFilteringVoice?.language.roundImage
        }
        type="image"
      />
    );
  }, [currentFilteringVoiceCode, voicesUsed, currentFilteringVoice]);

  const titleFilterBlockVoice = useMemo(() => {
    if (!currentFilteringVoice || currentFilteringVoiceCode === '') {
      return t('filterByVoice');
    }
    return t(currentFilteringVoice?.name) || t('filterByVoice');
  }, [currentFilteringVoice, t]);

  const filtersContent = (
    <>
      <DropdownCustom
        startIcon={startIconFilterBlockVoice}
        onChange={(value) => {
          if (value === currentFilteringVoiceCode) return;
          if (value)
            trackFilterApplied({
              filterType: MOE_FILTER_TYPE.VOICE,
              filterValue: value,
            });
          dispatch(actions.project.setFilterVoiceCode(value));
          dispatch(actions.project.setStartFilterBlock(true));
        }}
        searchable
        searchPlaceholder={t('search')}
        variant="block-voice"
        options={voicesUsed?.map((item) => ({
          label: item?.name,
          value: item?.code,
          labelCustom: (
            <StyledItemVoice>
              <div style={{ marginRight: '8px' }}>
                {item?.code === '' ? (
                  <ManageAccountsRoundedIcon
                    sx={{
                      width: 24,
                      height: 24,
                      color: COLOR.navyBlue,
                    }}
                  />
                ) : (
                  <BadgeAvatar
                    width={24}
                    img={item?.roundImage}
                    smallImg={item?.language && item?.language.roundImage}
                    type="image"
                  />
                )}
              </div>
              <Typography variant="body-14-medium" color={COLOR.darkGray}>
                {t(item?.name)}
              </Typography>
            </StyledItemVoice>
          ),
          disabled: !getNumberOfVoices(item?.code),
        }))}
        title={
          <Typography variant="body-14-medium" color={COLOR.indigo.default}>
            {titleFilterBlockVoice}
          </Typography>
        }
        value={currentFilteringVoiceCode}
      />

      <DropdownCustom
        variant="block-status"
        options={blockStatusOptions}
        onChange={(value) => handleChangeBlockStatus(value)}
        title={
          <Typography variant="body-14-medium" color={COLOR.indigo.default}>
            {
              blockStatusOptions.find(
                (item) => item.value === currentFilteringStatus,
              )?.label
            }
          </Typography>
        }
        value={currentFilteringStatus}
      />
      <Tooltip title={t('refershFilterBlockStatus')} placement="bottom" arrow>
        <IconButton className="refresh-button" onClick={handleRefresh}>
          <RefreshRoundedIcon className="refresh-icon" />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <>
      <StyledToolbar>
        {isSkeletonUI ? (
          <div className="toolbar-loading">
            <CircularProgress
              className="toolbar-loading-spinner"
              size={20}
              thickness={5}
            />
            <Typography variant="body-14-medium" color={COLOR.blue600}>
              {t('presentationProcessingProgress', {
                done: extractionProgress?.done ?? 0,
                total: extractionProgress?.total ?? 0,
                unit: pluralize(
                  extractionProgress?.total,
                  t('slide'),
                  language,
                ),
              })}
            </Typography>
          </div>
        ) : (
          <div className="toolbar">
            <ConfigMultiBlock
              handleConvertBlockSelected={handleConvertBlockSelected}
              filterBlockResults={filterBlockResults}
            />

            {!selectedBlockIds?.length &&
              (slideV2Enabled ? (
                <div className="sub-cta">
                  <div className="scroll-area" ref={scrollRef}>
                    <span className="scroll-spacer" />
                    <AIGenerateButton
                      loading={aiButtonLoading}
                      disabled={aiButtonDisabled}
                      onClick={handleAIGenerateClick}
                    />
                    <span className="toolbar-divider" />
                    {filtersContent}
                  </div>
                  {canScrollLeft && (
                    <div className="scroll-edge-wrap left">
                      <IconButton
                        className="scroll-edge"
                        onClick={() => scrollByStep(-1)}
                      >
                        <ChevronLeftRoundedIcon className="scroll-edge-icon" />
                      </IconButton>
                    </div>
                  )}
                  {canScrollRight && (
                    <div className="scroll-edge-wrap right">
                      <IconButton
                        className="scroll-edge"
                        onClick={() => scrollByStep(1)}
                      >
                        <ChevronRightRoundedIcon className="scroll-edge-icon" />
                      </IconButton>
                    </div>
                  )}
                </div>
              ) : (
                <div className="toolbar-container">{filtersContent}</div>
              ))}
          </div>
        )}
      </StyledToolbar>
      {slideV2Enabled && (
        <AIGenerateDialog
          open={Boolean(aiDialogOpen)}
          targetBlockId={aiDialogTargetBlockId}
          onClose={onCloseAIGenerateDialog}
          onSaveProject={onSaveProject}
        />
      )}
    </>
  );
};

export default Toolbar;
